import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BackendPagination } from "../models/pagination.model";
import { TableFilterType } from "../models/components/table/table-filter.model";
import {
	DataTableSortOrderType,
	ITablePagination,
} from "../models/components/table/table-pagination.model";
import { Task } from "../models/entities/task.model";
import { useTasks } from "./useTasks";
import { useTasksCount } from "./useTasksCount";

/**
 * Participant-scoped "Requires attention" alerts (KAN-2355).
 *
 * There is no separate alerts endpoint: the population-level Requires attention
 * table and this hook read the SAME rows from `/api/tasks/` — tasks with
 * `category=alert` and `resolved=false`. The only difference here is the extra
 * `participantId` filter, which the data-retriever validates server-side.
 *
 * Kept as a standalone hook (rather than inlined in the tab) so other surfaces
 * — e.g. the compact profile-sidebar card in KAN-2384 — consume exactly the
 * same query, filters and cache entries, and can never drift from the tab.
 */

export const PARTICIPANT_ALERTS_QUERY_KEYS = ["participants-tasks", "participants-tasks-count"];

export const PARTICIPANT_ALERTS_DEFAULT_PAGINATION: ITablePagination = {
	first: 0,
	rows: 10,
	page: 0,
	// Ordered by created date, newest first (KAN-2355 acceptance criteria).
	sortField: "taskDate",
	sortOrder: -1 as DataTableSortOrderType,
	search: undefined,
};

/**
 * Build the backend pagination for one participant's active alerts. Exported so
 * callers that need the raw query params (or a second query against the same
 * filters) don't have to re-derive the filter set.
 */
export const buildParticipantAlertsPagination = (
	participantId: number,
	pagination?: Partial<ITablePagination>
): BackendPagination =>
	new BackendPagination({
		...PARTICIPANT_ALERTS_DEFAULT_PAGINATION,
		...pagination,
		filters: [
			{
				field: "participantId",
				type: TableFilterType.STRING,
				value: String(participantId),
			},
			{
				field: "category",
				type: TableFilterType.SELECT_MULTIPLE,
				value: ["alert"],
			},
			{
				field: "resolved",
				type: TableFilterType.SELECT_MULTIPLE,
				value: ["false"],
			},
		],
	});

export interface UseParticipantAlertsResult {
	alerts: Task[];
	count: number;
	isLoading: boolean;
	error: unknown;
	/** Refetch this participant's alerts AND the population-level table. */
	invalidate: () => void;
}

export const useParticipantAlerts = (
	participantId: number,
	pagination?: Partial<ITablePagination>
): UseParticipantAlertsResult => {
	const queryClient = useQueryClient();

	// Serialised so callers can pass an inline object without re-fetching on
	// every render.
	const paginationKey = JSON.stringify(pagination ?? {});
	const backendPagination = useMemo(
		() => buildParticipantAlertsPagination(participantId, pagination),
		[participantId, paginationKey]
	);

	const tasksQuery = useTasks(backendPagination);
	const countQuery = useTasksCount(backendPagination);

	return {
		alerts: tasksQuery.data ?? [],
		count: countQuery.data ?? 0,
		isLoading: tasksQuery.isLoading,
		error: tasksQuery.error,
		// Resolving/editing writes the same row the population-level table reads,
		// so both caches are invalidated together.
		invalidate: () =>
			PARTICIPANT_ALERTS_QUERY_KEYS.forEach((key) =>
				queryClient.invalidateQueries({ queryKey: [key] })
			),
	};
};
